import { useState } from "react";
import { t } from "@lingui/core/macro";

import { UserAvatar } from "_/components/avatar";
import { DateTime } from "_/components/date-time";
import { HandoffLink } from "_/components/handoff-link";

import {
  SubmitCancelButtons,
  useModal,
  UseModalResult,
} from "_/components/modal";
import { ColumnDef, Table } from "_/components/table";
import { useAddToast } from "_/components/toasts";
import { Icon } from "_/components/icon";

import { UserDetailForm } from "_/components/settings-view";
import { UuidTooltip } from "_/components/uuid-tooltip";

import { Roles } from "_/components/rbac";
import { testPermissions } from "_/components/permission-test";
import { MeatballMenu, MenuItem, MenuItems } from "_/components/overlay-menu";

import {
  Org,
  OrganizationUser,
  useOrgUsers,
  useRemoveMember,
} from "_/data/orgs";
import { useMailEnabled } from "_/data/server";
import { User, useCurrentUser, useResetUserPassword } from "_/data/users";
import { useAssignableRoles, useCurrentUserPermissions } from "_/data/rbac";

import { getLanguageName } from "_/i18n";

import * as S from "./styled";

interface UserAndOrg {
  user: User;
  org: Org;
  canEditUserInfo?: boolean;
}

const UserEditModal = (props: UseModalResult<UserAndOrg>) => {
  const { Modal, modalData, closeModal } = props;
  const { user, org, canEditUserInfo } = modalData;
  const { data: assignableRoles = [] } = useAssignableRoles(org.domainId);

  return (
    <Modal title={t`components.admin-view.edit-user`}>
      <UserDetailForm
        user={user}
        adminData={{ org, assignableRoles, canEditUserInfo }}
        onClose={closeModal}
      />
    </Modal>
  );
};

const UserRemoveModal = (props: UseModalResult<UserAndOrg>) => {
  const { Modal, closeModal, modalData } = props;
  const removeMember = useRemoveMember();
  const { org, user } = modalData;

  return (
    <Modal>
      <S.UserRemoveModalContainer>
        <S.UserRemoveModalIcon>
          <Icon variant="Delete" />
        </S.UserRemoveModalIcon>
        <h2>{t`modals.user-removal-confirmation.title`}</h2>
        <p>
          {t`modals.user-removal-confirmation.description ${user.name} ${org.name}`}
        </p>
      </S.UserRemoveModalContainer>
      <SubmitCancelButtons
        submitButtonLabel={t`common.remove`}
        disableSubmit={false}
        onSubmit={async () => {
          await removeMember.mutateAsync({
            orgId: org.id,
            userId: user.id,
          });
          closeModal();
        }}
        onCancel={closeModal}
      />
    </Modal>
  );
};

// Emails the user a reset link, or shows the link for the admin to hand over
// when the install cannot send email.
const UserResetPasswordModal = (props: UseModalResult<UserAndOrg>) => {
  const { Modal, closeModal, modalData } = props;
  const resetUserPassword = useResetUserPassword();
  const addToast = useAddToast();
  const mailEnabled = useMailEnabled();
  const { user } = modalData;
  const [resetLink, setResetLink] = useState<string | undefined>();
  const [expiresAt, setExpiresAt] = useState<string | undefined>();

  const description =
    mailEnabled === false
      ? t`modals.user-reset-password.link-description ${user.name}`
      : t`modals.user-reset-password.email-description ${user.name}`;

  async function resetPassword() {
    const result = await resetUserPassword.mutateAsync({ userId: user.id });

    if (result.resetLink) {
      setResetLink(result.resetLink);
      setExpiresAt(result.expiresAt);
      return;
    }

    addToast({
      kind: "success",
      title: t`modals.user-reset-password.sent-toast.title`,
      content: t`modals.user-reset-password.sent-toast.content ${user.email}`,
    });
    closeModal();
  }

  if (resetLink) {
    const expiry = <DateTime value={expiresAt} />;
    return (
      <Modal title={t`modals.user-reset-password.title`}>
        <HandoffLink
          description={t`modals.user-reset-password.link-ready ${user.name}`}
          link={resetLink}
          footnote={
            <>
              {t`modals.user-reset-password.expires`} {expiry}
            </>
          }
          onClose={closeModal}
        />
      </Modal>
    );
  }

  return (
    <Modal title={t`modals.user-reset-password.title`}>
      <S.UserRemoveModalContainer>
        <p>{description}</p>
      </S.UserRemoveModalContainer>
      <SubmitCancelButtons
        submitButtonLabel={t`modals.user-reset-password.submit`}
        disableSubmit={resetUserPassword.isPending}
        onSubmit={resetPassword}
        onCancel={closeModal}
      />
    </Modal>
  );
};

export const UsersList = ({ org }: { org: Org }) => {
  const { data: orgUsers, isLoading } = useOrgUsers(org.id);
  const { data: currentUser } = useCurrentUser();
  const userEdit = useModal<UserAndOrg>();
  const userRemove = useModal<UserAndOrg>();
  const userResetPassword = useModal<UserAndOrg>();

  const { data: permissions } = useCurrentUserPermissions(org.domainId);

  const hasUsersWrite = testPermissions("users/write", permissions);
  const canManageRoles = testPermissions("roles/write", permissions);

  const canEdit = hasUsersWrite || canManageRoles;

  const canRemoveMember = testPermissions(
    "organizations/removeMember",
    permissions
  );

  if (orgUsers?.length === 0) {
    return null;
  }

  const menuItemsFor = (rowData: OrganizationUser): MenuItem[] => {
    const edit: MenuItem = {
      render: t`common.edit`,
      icon: "Edit",
      onClick: () =>
        userEdit.openModal({
          user: rowData.user,
          org,
          canEditUserInfo: hasUsersWrite,
        }),
      disabled: !canEdit,
    };

    const resetPassword: MenuItem = {
      render: t`components.admin-view.reset-password`,
      icon: "Key",
      onClick: () => userResetPassword.openModal({ user: rowData.user, org }),
      disabled: !canRemoveMember,
    };

    const remove: MenuItem = {
      render: t`common.remove`,
      icon: "Delete",
      onClick: () => userRemove.openModal({ user: rowData.user, org }),
      disabled: !canRemoveMember || rowData.user.id === currentUser.id,
    };

    return [edit, resetPassword, MenuItems.SEPARATOR, remove];
  };

  const columns: ColumnDef<OrganizationUser>[] = [
    {
      id: "name",
      header: t`common.name`,
      accessorKey: "user.name",
      cell: ({ row: { original: rowData } }) => {
        const user = rowData.user;
        if (!user) return null;
        return (
          <S.UserCell>
            <UserAvatar resource={user} size="small" />
            <span>{user.name}</span>
          </S.UserCell>
        );
      },
    },
    {
      id: "email",
      header: t`common.email`,
      accessorKey: "user.email",
    },
    {
      id: "phone",
      header: t`common.phone`,
      accessorKey: "user.phone",
    },
    {
      id: "language",
      header: t`common.language`,
      accessorKey: "user.language",
      cell: ({ row: { original: rowData } }) =>
        getLanguageName(rowData.user.language),
    },
    {
      id: "verified",
      header: t`common.verified`,
      accessorKey: "user.verified",
      cell: ({ cell }) => cell.getValue() && <S.Checkmark>✓</S.Checkmark>,
    },
    {
      id: "roles",
      header: t`common.roles`,
      cell: ({ row: { original: rowData } }) => (
        <Roles user={rowData.user} org={org} showGlobalRoles={true} />
      ),
    },
    {
      id: "last_authenticated",
      header: t`common.last-authenticated`,
      accessorKey: "user.lastAuthenticated",
      cell: ({ row: { original: rowData } }) => (
        <DateTime value={rowData.user.lastAuthenticated} />
      ),
    },
    {
      id: "id",
      header: t`common.id`,
      accessorKey: "user.id",
      cell: ({ row: { original: rowData } }) => (
        <UuidTooltip uuid={rowData.user.id} />
      ),
    },
    {
      id: "actions",
      header: t`common.actions`,
      cell: ({ row: { original: rowData } }) => (
        <MeatballMenu items={menuItemsFor(rowData)} />
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        data={(orgUsers ?? []).filter((ou) => ou.active)}
        loading={isLoading}
        initialState={{ sorting: [{ id: "name", desc: false }] }}
      />

      {/* Single modal instances, outside the table */}
      {userEdit.isModalOpen && <UserEditModal {...userEdit} />}
      {userRemove.isModalOpen && <UserRemoveModal {...userRemove} />}
      {userResetPassword.isModalOpen && (
        <UserResetPasswordModal {...userResetPassword} />
      )}
    </>
  );
};
